import moment from 'moment';
import { parseToRgb, darken } from 'polished';

const DEFAULT_PRIMARY = '#20A26B';
export const ACCESSIBLE_PRIMARY_FILL = '#177A50';

/**
 * Returns the color if it's valid (hex, rgb, rgba, hsl, hsla), otherwise returns fallback.
 * Use this when passing theme/branding colors to polished functions that throw on invalid input.
 */
export function toValidColor(color, fallback = DEFAULT_PRIMARY) {
    if (!color || typeof color !== 'string') return fallback;
    try {
        parseToRgb(color);
        return color;
    } catch {
        return fallback;
    }
}

function srgbChannel(c) {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

function relativeLuminance(color) {
    const { red, green, blue } = parseToRgb(toValidColor(color));
    return (
        0.2126 * srgbChannel(red) +
        0.7152 * srgbChannel(green) +
        0.0722 * srgbChannel(blue)
    );
}

export function contrastRatio(fg, bg) {
    const L1 = relativeLuminance(fg);
    const L2 = relativeLuminance(bg);
    const lighter = Math.max(L1, L2);
    const darker = Math.min(L1, L2);
    return (lighter + 0.05) / (darker + 0.05);
}

/** Darken a fill color until white text meets WCAG AA contrast (4.5:1). */
export function accessibleButtonFill(color, minRatio = 4.5) {
    const c = toValidColor(color, DEFAULT_PRIMARY);
    try {
        if (contrastRatio('#FFFFFF', c) >= minRatio) return c;
        for (let amount = 0.04; amount <= 0.7; amount += 0.04) {
            const darkened = darken(amount, c);
            if (contrastRatio('#FFFFFF', darkened) >= minRatio) return darkened;
        }
    } catch {
        return ACCESSIBLE_PRIMARY_FILL;
    }
    return ACCESSIBLE_PRIMARY_FILL;
}

/** Example: January 11, 2021 */
export function formatDate(dateString) {
    if (!dateString) {
        return '';
    }
    try {
        return moment(dateString).format('LL');
        //const dt = new Date(dateString);
        //const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        //return dt.getDate() + ' ' + monthNames[dt.getMonth()] + ' ' + dt.getFullYear();
    } catch (e) {
        return '';
    }
}

export function joinPath(...parts) {
    if (!parts) return '';
    let result = '';
    for (let i = 0; i < parts.length; i++) {
        result +=
            parts[i].startsWith('/') && i > 0 ? parts[i].substr(1) : parts[i];

        if (i !== parts.length - 1 && !result.endsWith('/')) result += '/';
    }

    return result;
}

export function resourcePath(path) {
    if (!path) return path;
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    if (path.startsWith('/static')) {
        return joinPath("https://resources.awarego.com/", path.replace('/static', ''));
    }
    return path;
}

/** Resolve an image path through i18n (translation overrides) then resourcePath. */
export function translatableResourcePath(imageKey, t) {
    if (!imageKey) return imageKey;
    if (!t) return resourcePath(imageKey);
    if (Array.isArray(imageKey)) {
        return imageKey.map((key) => translatableResourcePath(key, t));
    }
    const translated = t(imageKey);
    return resourcePath(translated || imageKey);
}

export const primaryColor = (props) =>
    toValidColor(props?.color || props?.theme?.primary, DEFAULT_PRIMARY);

export function calculateColor(number) {
    if (number) {
        if (number <= 24) return '#EB5757';
        if (number <= 49) return '#FF8440';
        if (number <= 74) return '#F2C94C';
        if (number <= 89) return '#8CBD00';
        return '#20A26B';
    } else {
        return '#EB5757';
    }
}

export function calculateScoreTitle(percentage) {
    if (percentage <= 24) return 'Very risky';
    if (percentage <= 49) return 'Risky';
    if (percentage <= 74) return 'Fair';
    if (percentage <= 89) return 'Very good';
    return 'Excellent';
}

export function defaultZero(value) {
    return value || 0;
}

export function calculateSeverityColor(variant) {
    //if (variant === 'error') return 'danger';
    return variant;
}
